import { useState } from 'react';
import { ErrorBoundary as BaseErrorBoundary } from 'react-error-boundary';

import { IntentionPromptSharedModal } from '@grow-therapy-team/seedling-components';

import { logger } from '../../datadog';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { useSendSessionIntentionPromptCallback } from '../messages/useSendSessionIntentionPromptCallback';
import { IntentionNotSetModalWrapper } from './IntentionNotSetModal';
import { IntentionSetModalWrapper } from './IntentionSetModal';
import { IntentionSettingButtonWrapper as IntentionSettingButton } from './IntentionSettingButton';
import { useIntentionState } from './useIntentionState';
import { IntentionUse } from './useIntentionUse';

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return <BaseErrorBoundary FallbackComponent={() => null}>{children}</BaseErrorBoundary>;
}

const IntentionPromptModal = ({
  appVersion,
  isOpen,
  onClose,
  use,
  clientName,
  initials,
  providerImage,
  clientUserShortId,
  patientShortId,
  providerShortId,
  appointmentShortId,
  onSendPrompt,
}: {
  appVersion: string;
  isOpen: boolean;
  onClose: () => void;
  use: IntentionUse;
  clientName: string;
  initials: string;
  providerImage: string;
  clientUserShortId: string;
  patientShortId: string;
  providerShortId: string;
  appointmentShortId: string;
  onSendPrompt: (appointmentShortId: string) => void;
}) => {
  switch (use) {
    case 'not-set':
    case 'not-set-active':
      return (
        <IntentionNotSetModalWrapper
          clientName={clientName}
          isOpen={isOpen}
          onClose={onClose}
          providerImage={providerImage}
          onSendPrompt={() => onSendPrompt(appointmentShortId)}
          patientShortId={patientShortId}
          appVersion={appVersion}
          clientUserShortId={clientUserShortId}
          appointmentShortId={appointmentShortId}
        />
      );
    case 'shared':
      return (
        <IntentionPromptSharedModal
          isOpen={isOpen}
          onClose={onClose}
          name={clientName}
          providerPhotoUrl={providerImage}
          onResendPrompt={() => onSendPrompt(appointmentShortId)}
        />
      );
    case 'set':
      return (
        <IntentionSetModalWrapper
          isOpen={isOpen}
          onClose={onClose}
          clientName={clientName}
          initials={initials}
          appVersion={appVersion}
          clientUserShortId={clientUserShortId}
          patientShortId={patientShortId}
          providerShortId={providerShortId}
          appointmentShortId={appointmentShortId}
        />
      );
    default:
      return <></>;
  }
};

export function IntentionSettingWrapper() {
  const [isOpen, setIsOpen] = useState(false);

  const intentionState = useIntentionState();

  const sendSessionIntentionPrompt = useSendSessionIntentionPromptCallback();
  const { userClicked } = useTrackEvent();

  const handleButtonClick = () => {
    userClicked(EventTypeEnum.INTENTION_SETTING_PILL_CLICKED, {
      appointmentShortId,
      intentionState: use,
    });
    setIsOpen(true);
  };

  const handleSendPrompt = async (appointmentShortId: string) => {
    try {
      userClicked(EventTypeEnum.INTENTION_PROMPT_SENT, {
        appointmentShortId,
      });
      setIsOpen(false);
      await sendSessionIntentionPrompt(1);
    } catch (error) {
      logger.error('Error sending session intention prompt', { error });
    }
  };

  if (!intentionState || intentionState?.isLoading) {
    return null;
  }

  const {
    use,
    appVersion,
    appointmentShortId,
    preferredName,
    initials,
    providerImage,
    clientUserShortId,
    patientShortId,
    providerShortId,
  } = intentionState;

  return (
    <ErrorBoundary>
      <div className='absolute top-2 left-2'>
        <IntentionPromptModal
          appVersion={appVersion}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          clientName={preferredName}
          initials={initials}
          providerImage={providerImage}
          use={use}
          clientUserShortId={clientUserShortId}
          patientShortId={patientShortId}
          providerShortId={providerShortId}
          appointmentShortId={appointmentShortId}
          onSendPrompt={handleSendPrompt}
        />
        <IntentionSettingButton
          use={use}
          onClick={handleButtonClick}
          clientName={preferredName}
          appointmentShortId={appointmentShortId}
        />
      </div>
    </ErrorBoundary>
  );
}
